import { ErrorRounded, ExpandMoreRounded, MyLocationRounded } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Box, Button, IconButton, Popover, Stack, Tooltip, Typography } from '@mui/material'
import { MouseEvent, SyntheticEvent, useCallback, useState } from 'react'
import { INode, NodeType } from '../../definitions/Nodes'
import SmartTypography from '../controls/SmartTypography'
import { useReactFlow } from '@xyflow/react'

interface P {
    nodes: INode<NodeType>[]
    errors: { nodeId: string, nodeError: string }[]
}

const ErrorsNodePopover = ({ nodes, errors }: P) => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)
    const [expanded, setExpanded] = useState<string | false>(false)
    const { fitView } = useReactFlow()
    const open = Boolean(anchorEl)

    const handleClick = useCallback((e: MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(e.currentTarget)
    }, [])

    const handleClose = useCallback(() => {
        setExpanded(false)
        setAnchorEl(null)
    }, [])

    const handleAccordionChange = useCallback((panel: string) => (_: SyntheticEvent, isExpanded: boolean) => {
        setExpanded(isExpanded ? panel : false)
    }, [])

    const handleFocusNode = useCallback((nodeId: string) => (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation()
        setExpanded(false)
        setAnchorEl(null)
        fitView({ nodes: [{ id: nodeId }], duration: 1500 })
    }, [])

    return (
        <>
            <IconButton
                color='error'
                onClick={handleClick}
                sx={{
                    'border': '1px solid var(--color-bad)',
                    'borderRadius': 1,
                    '& .MuiTouchRipple-root span': { borderRadius: 1 },
                    '&.MuiIconButton-root': { backgroundColor: theme => theme.palette.background.default }
                }}>
                <ErrorRounded />
            </IconButton>
            <Popover
                elevation={3}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                slotProps={{
                    paper: {
                        sx: {
                            display: 'flex',
                            flexDirection: 'column',
                            maxWidth: 400,
                            maxHeight: 500,
                            padding: 2
                        }
                    }
                }}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left'
                }}
                transformOrigin={{
                    vertical: -6,
                    horizontal: 0
                }}>
                <Typography variant='h6' marginBottom={2}>
                    Errors List
                </Typography>
                <Stack spacing={2} sx={{ overflowY: 'auto' }} flex={1}>
                    <Stack>
                        {errors.map(err => (
                            <Accordion
                                key={err.nodeId}
                                expanded={expanded === err.nodeId}
                                onChange={handleAccordionChange(err.nodeId)}
                                elevation={3}
                                disableGutters
                                sx={{
                                    '&.MuiAccordion-root': { borderRadius: 0 },
                                    'borderLeft': 0,
                                    'borderRight': 0,
                                    '&:not(:last-child)': { borderBottom: 0 },
                                    '&::before': { display: 'none' }
                                }}>
                                <AccordionSummary
                                    sx={{ '.MuiAccordionSummary-content': { minWidth: 0 } }}
                                    expandIcon={<ExpandMoreRounded fontSize='small' />}>
                                    <Box display='flex' gap={1} sx={{ minWidth: 0, alignItems: 'center' }}>
                                        <Tooltip title='Go to error' arrow>
                                            <IconButton
                                                size='small'
                                                onClick={handleFocusNode(err.nodeId)}>
                                                <MyLocationRounded fontSize='small' />
                                            </IconButton>
                                        </Tooltip>
                                        <SmartTypography variant='subtitle2' truncateAtWidth={180} flexShrink={0}>
                                            {nodes.find(n => n.uuid === err.nodeId).name}
                                        </SmartTypography>
                                        <SmartTypography variant='body2' color='text.secondary'>
                                            {nodes.find(n => n.uuid === err.nodeId).type}
                                        </SmartTypography>
                                    </Box>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Stack spacing={1}>
                                        <Stack flexDirection='row' gap={1}>
                                            <ErrorRounded fontSize='small' color='error' />
                                            <Typography variant='body2'>{err.nodeError}</Typography>
                                        </Stack>
                                    </Stack>
                                </AccordionDetails>
                            </Accordion>
                        ))}
                    </Stack>
                </Stack>
                <Box display='flex' justifyContent='flex-end' marginTop={2}>
                    <Button color='secondary' size='small' variant='text' onClick={handleClose}>
                        close
                    </Button>
                </Box>
            </Popover>
        </>
    )
}

export default ErrorsNodePopover
